/**
 * Module dependencies.
 */

import {
  ProductColor,
  ProductFinish,
  ProductFormat,
  ProductFunction,
  ProductProps,
  ProductThickness,
  ProductTypology
} from 'src/types/products';

import { color, media, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { recycleMaterial as recycleMaterialValues } from 'src/core/collections-config/recycle-material';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Filters from './filters';
import FiltersDisclosuresProvider from 'src/context/filters-disclosures/provider';
import Loading from 'src/components/core/loading';
import ProductCard from 'src/components/cards/product-card';
import React, { Dispatch, Fragment, ReactElement, SetStateAction, useEffect, useMemo } from 'react';
import Type from 'src/components/core/typography';
import camelCase from 'lodash/camelCase';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import styled, { css } from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useProductsCollection from 'src/api/app/products/use-products-collection';

/**
 * `Props` type.
 */

type Props = {
  collectionId: string;
  isKiosk?: boolean;
  onForceUpdate: Dispatch<SetStateAction<boolean>>;
  productColors: ProductColor[];
  productFinishes: ProductFinish[];
  productFormats: ProductFormat[];
  productFunctions: ProductFunction[];
  productThickness: ProductThickness[];
  productTypologies: ProductTypology[];
  products: {
    pageParams: number[];
    pages: Array<{
      data: ProductProps[];
      nextPage: number | null;
      total: number;
    }>;
  };
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ hasData: boolean }>`
  background-color: ${color('white')};
  padding: ${units(13)} 0 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0 0;
  `}

  ${ifProp(
    'hasData',
    css`
      padding-bottom: ${units(6)};

      ${media.min('ms')`
      padding-bottom: ${units(18)};
    `}
    `
  )}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title    .'
      '. filters  .'
      '. list     .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(6)};
`;

/**
 * `FiltersWrapper` styled component.
 */

const FiltersWrapper = styled.div`
  grid-area: filters;
  margin-bottom: ${units(8)};
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.div`
  grid-area: list;
  min-height: 250px;
  position: relative;
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  top: 100px;
  transform: translateX(-50%);
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  text-align: center;
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: grid;
  grid-area: list;
  grid-column-gap: ${units(2.5)};
  grid-template-columns: 100%;

  ${media.min('xxs')`
    grid-template-columns: repeat(2, minmax(${units(16)}, 1fr));
  `}

  ${media.min('sm')`
    grid-column-gap: ${units(5)};
  `}

  ${media.min('ms')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('md')`
    grid-column-gap: ${units(7.5)};
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('xl')`
    grid-column-gap: ${units(10)};
  `}
`;

/**
 * `StyledProductCard` styled component.
 */

const StyledProductCard = styled(ProductCard)`
  margin-bottom: ${units(7)};
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  padding: ${units(13)} 0;
  text-align: center;
`;

/**
 * `ProductsList` component.
 */

const ProductsList = (props: Props): ReactElement => {
  const {
    collectionId,
    isKiosk,
    onForceUpdate,
    productColors,
    productFinishes,
    productFormats,
    productFunctions,
    productThickness,
    productTypologies,
    products
  } = props;

  const { translate } = useTranslate();
  const { submittedFilters } = useFiltersList();
  const routeResolver = useLocalizedRoute();
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, isSuccess, refetch } =
    useProductsCollection({
      collectionId,
      filterParams: submittedFilters,
      initialData: products
    });

  const total = data?.pages[0]?.total ?? 0;
  const isUpdating = isFetchingNextPage || isFetching;
  const productRecycleMaterial = useMemo(() => {
    const recycleMaterialSet = new Set<string>();
    const validValues = Object.values(recycleMaterialValues);
    
    // Process both initial data and fetched data
    const allPages = data?.pages || products?.pages || [];
    
    if (!allPages || allPages.length === 0) {
      return [];
    }
    
    map(allPages, (page) => {
      if (!page?.data || !Array.isArray(page.data)) {
        return;
      }
      
      map(page.data, (product: ProductProps) => {
        if (!product) {
          return;
        }
        
        // Access recycle_material field (can come as snake_case or camelCase from API)
        const recycleMaterialKey = Object.keys(product).find(key => {
          const normalizedKey = camelCase(key);

          return normalizedKey === 'recycleMaterial' || key === 'recycle_material';
        });
        
        const recycleMaterial = recycleMaterialKey ? (product as any)[recycleMaterialKey] : null;
        
        if (recycleMaterial !== null && recycleMaterial !== undefined && recycleMaterial !== '') {
          const value = String(recycleMaterial).trim();
          // Also check if value ends with % and remove it
          const cleanValue = value.replace(/%$/, '').trim();
          
          // Check both clean value and original value
          if (validValues.includes(cleanValue as any)) {
            recycleMaterialSet.add(cleanValue);
          } else if (validValues.includes(value as any)) {
            recycleMaterialSet.add(value);
          }
        }
      });
    });
    
    return Array.from(recycleMaterialSet).sort((first, second) => parseFloat(first) - parseFloat(second));
  }, [data, products]);

  const productThicknessFromProducts = useMemo(() => {
    const allPages = data?.pages || products?.pages || [];

    if (!allPages.length) {
      return [];
    }

    const thicknesses: ProductThickness[] = [];

    map(allPages, (page) => {
      if (!page?.data || !Array.isArray(page.data)) {
        return;
      }

      map(page.data, (product: ProductProps) => {
        const thicknessKey = Object.keys(product || {}).find(
          (key) => camelCase(key) === 'thickness' || key === 'thickness'
        );

        const raw = thicknessKey ? (product as Record<string, unknown>)[thicknessKey] : null;

        if (!raw || typeof raw !== 'object' || !('id' in raw) || !('name' in raw)) {
          return;
        }

        const thickness = raw as ProductThickness;

        thicknesses.push({ id: thickness.id, name: thickness.name });
      });
    });

    return sortBy(uniqBy(thicknesses, 'id'), [(item: ProductThickness) => parseFloat(item.name) || 0]);
  }, [data, products]);

  const availableFiltersList = useMemo(
    () => ({
      typologies: productTypologies || [],
      functions: productFunctions || [], // eslint-disable-line sort-keys
      formats: productFormats || [], // eslint-disable-line sort-keys
      colors: productColors || [], // eslint-disable-line sort-keys
      finishes: productFinishes || [], // eslint-disable-line sort-keys
      recycleMaterial: productRecycleMaterial || [], // eslint-disable-line sort-keys
      thickness: productThicknessFromProducts.length > 0 ? productThicknessFromProducts : (productThickness || []) // eslint-disable-line sort-keys
    }),
    [
      productColors,
      productFinishes,
      productFormats,
      productFunctions,
      productTypologies,
      productThickness,
      productThicknessFromProducts,
      productRecycleMaterial
    ]
  );

  useEffect(() => {
    if (!isUpdating) {
      onForceUpdate(true);

      const timeout = setTimeout(() => {
        onForceUpdate(false);
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [isUpdating, onForceUpdate]);

  return (
    <FiltersDisclosuresProvider>
      <Section hasData={total > 0 && isSuccess}>
        <Container hasPadding={!isKiosk}>
          <Grid>
            <Title>
              <FadeInUpAnimation>{translate('collections:details.productsList.title')}</FadeInUpAnimation>
            </Title>

            <FiltersWrapper>
              <Filters
                availableFiltersList={availableFiltersList}
                isUpdating={isUpdating}
                isUpdatingSucceed={isSuccess}
                onUpdate={refetch}
              />
            </FiltersWrapper>

            {isSuccess && (
              <ListWrapper>
                <StyledLoading active={isUpdating} />

                {!isLoading && !isUpdating && total === 0 && (
                  <Empty>
                    <Type.H6 paddingVertical={units(1)}>
                      <FadeInUpAnimation>{translate('collections:details.productsList.emptyList')}</FadeInUpAnimation>
                    </Type.H6>
                  </Empty>
                )}

                {total > 0 && (
                  <>
                    <List>
                      {map(data?.pages, (products, index: number) => (
                        <Fragment key={index}>
                          {map(products?.data, (product: ProductProps) => {
                            const kioskRoute = routeTemplate(kioskRoutes.productDetails, { slug: product.slug });
                            const route = routeResolver('productDetails', { slug: product.slug });

                            return (
                              <FadeInUpAnimation key={product.id}>
                                <StyledProductCard
                                  hasRoomvo
                                  isKioskCard={isKiosk}
                                  url={isKiosk ? kioskRoute : route}
                                  {...product}
                                />
                              </FadeInUpAnimation>
                            );
                          })}
                        </Fragment>
                      ))}
                    </List>

                    {hasNextPage && (
                      <FadeInUpAnimation>
                        <LoadMore>
                          <Button disabled={isUpdating} isLoading={isUpdating} onClick={() => fetchNextPage()}>
                            {translate('common:actions.loadMore')}
                          </Button>
                        </LoadMore>
                      </FadeInUpAnimation>
                    )}
                  </>
                )}
              </ListWrapper>
            )}
          </Grid>
        </Container>
      </Section>
    </FiltersDisclosuresProvider>
  );
};

/**
 * Export `ProductsList` component.
 */

export default ProductsList;
